// Corpus Data Service
// Loads and queries corpus training data.
//
// DATA SOURCE: this site's own /corpus (see CORPUS_BASE below).
//
// Key files:
//   - comprehensive_measurements.json: All case study measurements
//   - docs.json: Document index (slug -> file paths)
//   - training_intent.json: AI guidance and key facts
//   - training_qa_pairs.json: Q&A training data
//   - decision_framework.json: Decision trees
//   - manifest.json: Corpus statistics
//
// The cache persists for the session. Clear cache by refreshing the page.

// Corpus files are served from this site's own /corpus, which
// scripts/sync_dashboard.py fills (after its obfuscation + measurement
// validators pass) and the deploy uploads. Never read from the public mirror:
// that bypassed the validated sync and kept serving stale data after a fix
// (INCIDENT-2026-11-02-cs058-case-study-source-audit).
const CORPUS_BASE = '/corpus'

// Cache for loaded data
const cache = {}

// Clear all cached data (useful after corpus updates)
export function clearCorpusCache() {
  Object.keys(cache).forEach(key => delete cache[key])
  console.log('Corpus cache cleared')
}

// Check if corpus data is cached
export function isCached(path) {
  return !!cache[path]
}

// Get cache status (for debugging)
export function getCacheStatus() {
  return Object.keys(cache).map(path => ({
    path,
    loaded: true,
    size: JSON.stringify(cache[path]).length
  }))
}

// Generic fetch with in-memory caching
async function fetchWithCache(path) {
  if (cache[path]) {
    return cache[path]
  }
  const response = await fetch(`${CORPUS_BASE}/${path}`)
  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status} ${response.statusText}`)
  }
  const data = await response.json()
  cache[path] = data
  return data
}

// Load corpus manifest (stats, document list)
export async function loadManifest() {
  return fetchWithCache('manifest.json')
}

// Load comprehensive measurements (all 133 case studies)
export async function loadMeasurements() {
  const data = await fetchWithCache('comprehensive_measurements.json')
  return data.records || []
}

// Load documents index (slug -> file paths)
export async function loadDocsIndex() {
  return fetchWithCache('docs.json')
}

// Load training intent (AI guidance, key facts)
export async function loadTrainingIntent() {
  return fetchWithCache('training_intent.json')
}

// Load training Q&A pairs
export async function loadTrainingQA() {
  return fetchWithCache('training_qa_pairs.json')
}

// Load decision framework
export async function loadDecisionFramework() {
  return fetchWithCache('decision_framework.json')
}

// Load equipment performance data
export async function loadEquipmentPerformance() {
  return fetchWithCache('equipment_performance.json')
}

// Load industry performance data
export async function loadIndustryPerformance() {
  return fetchWithCache('industry_performance.json')
}

// Load conversation examples
export async function loadConversationExamples() {
  return fetchWithCache('conversation_examples.json')
}

// Get a single measurement by ID
export async function getMeasurementById(studyId) {
  const measurements = await loadMeasurements()
  return measurements.find(m => m.study_id === studyId)
}

// Filter measurements by criteria
export async function filterMeasurements(filters = {}) {
  const measurements = await loadMeasurements()

  return measurements.filter(m => {
    // Filter by equipment type
    if (filters.equipmentType && filters.equipmentType !== 'all') {
      const eqArray = getEquipmentArray(m)
      const hasEquipment = eqArray.some(e =>
        e.type?.toLowerCase() === filters.equipmentType.toLowerCase()
      )
      if (!hasEquipment) return false
    }

    // Filter by industry
    if (filters.industry && filters.industry !== 'all') {
      if (m.site?.industry?.toLowerCase() !== filters.industry.toLowerCase()) {
        return false
      }
    }

    // Filter by state
    if (filters.state && filters.state !== 'all') {
      if (m.site?.state !== filters.state && m.state !== filters.state) {
        return false
      }
    }

    // Filter by quality
    if (filters.quality && filters.quality !== 'all') {
      if (m.extraction_quality !== filters.quality) {
        return false
      }
    }

    // Filter by category
    if (filters.category && filters.category !== 'all') {
      if (m.record_category !== filters.category) {
        return false
      }
    }

    // Filter by efficiency range
    if (filters.minEfficiency !== undefined) {
      const eff = m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct
      if (!eff || eff < filters.minEfficiency) return false
    }
    if (filters.maxEfficiency !== undefined) {
      const eff = m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct
      if (!eff || eff > filters.maxEfficiency) return false
    }

    return true
  })
}

// Helper to safely get equipment array
function getEquipmentArray(m) {
  if (Array.isArray(m.equipment)) return m.equipment
  if (m.equipment && typeof m.equipment === 'object') return [m.equipment]
  return []
}

/**
 * Calculate field completeness percentage for a measurement record
 * Counts how many of the 11 key data fields are populated
 * @param {Object} record - Raw measurement record from corpus
 * @returns {number} Percentage (0-100) of fields that are populated
 */
export function calculateFieldCompleteness(record) {
  const eqArray = getEquipmentArray(record)
  const firstEquip = eqArray[0] || {}

  const fields = [
    record.site?.industry,                                              // 1. Industry
    record.site?.state || record.state,                                 // 2. State
    firstEquip.type,                                                    // 3. Equipment Type
    firstEquip.age_years,                                               // 4. Equipment Age
    firstEquip.capacity_tons,                                           // 5. Capacity (tons)
    firstEquip.refrigerant,                                             // 6. Refrigerant
    record.measurements?.efficiency_improvement_pct ||                  // 7. Efficiency %
      record.measurements?.kw_per_ton?.improvement_pct,
    record.measurements?.kw_per_ton?.before,                            // 8. kW/Ton Before
    record.measurements?.kw_per_ton?.after,                             // 9. kW/Ton After
    record.financial?.annual_savings,                                   // 10. Annual Savings
    record.certification?.pe_name || record.pe_name                     // 11. PE Name
  ]

  const filledCount = fields.filter(f => f !== null && f !== undefined && f !== '').length
  return Math.round((filledCount / fields.length) * 100)
}

// Get unique values for filter dropdowns
export async function getFilterOptions() {
  const measurements = await loadMeasurements()

  const equipmentTypes = new Set()
  const industries = new Set()
  const states = new Set()
  const categories = new Set()
  const qualities = new Set()

  measurements.forEach(m => {
    getEquipmentArray(m).forEach(e => {
      if (e.type) equipmentTypes.add(e.type)
    })
    if (m.site?.industry) industries.add(m.site.industry)
    if (m.site?.state) states.add(m.site.state)
    if (m.state) states.add(m.state)
    if (m.record_category) categories.add(m.record_category)
    if (m.extraction_quality) qualities.add(m.extraction_quality)
  })

  return {
    equipmentTypes: Array.from(equipmentTypes).sort(),
    industries: Array.from(industries).sort(),
    states: Array.from(states).sort(),
    categories: Array.from(categories).sort(),
    qualities: Array.from(qualities).sort()
  }
}

// Get corpus statistics
export async function getCorpusStats() {
  const [manifest, measurements] = await Promise.all([
    loadManifest(),
    loadMeasurements()
  ])

  // Calculate stats from measurements
  const efficiencies = measurements
    .map(m => m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct)
    .filter(Boolean)

  const ages = measurements
    .flatMap(m => getEquipmentArray(m).map(e => e.age_years))
    .filter(Boolean)

  const capacities = measurements
    .flatMap(m => getEquipmentArray(m).map(e => e.capacity_tons))
    .filter(Boolean)

  const annualSavings = measurements
    .map(m => m.financial?.annual_savings)
    .filter(Boolean)

  return {
    totalRecords: manifest.record_categorization?.total_records || measurements.length,
    measurementSummary: manifest.measurement_summary || {},
    qualityBreakdown: measurements.reduce((acc, m) => {
      const quality = m.extraction_quality || 'unknown'
      acc[quality] = (acc[quality] || 0) + 1
      return acc
    }, {}),
    categoryBreakdown: measurements.reduce((acc, m) => {
      const cat = m.record_category || 'unknown'
      acc[cat] = (acc[cat] || 0) + 1
      return acc
    }, {}),
    efficiencyStats: {
      count: efficiencies.length,
      min: Math.min(...efficiencies),
      max: Math.max(...efficiencies),
      mean: efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length
    },
    ageStats: {
      count: ages.length,
      min: Math.min(...ages),
      max: Math.max(...ages),
      mean: ages.reduce((a, b) => a + b, 0) / ages.length
    },
    capacityStats: {
      count: capacities.length,
      min: Math.min(...capacities),
      max: Math.max(...capacities),
      mean: capacities.reduce((a, b) => a + b, 0) / capacities.length
    },
    financialStats: {
      count: annualSavings.length,
      totalSavings: annualSavings.reduce((a, b) => a + b, 0),
      avgSavings: annualSavings.reduce((a, b) => a + b, 0) / annualSavings.length
    }
  }
}

// Format measurement for display
export function formatMeasurement(m) {
  const eqArray = getEquipmentArray(m)
  const firstEquip = eqArray[0] || {}
  return {
    id: m.study_id,
    source: m.source_file,
    quality: m.extraction_quality,
    category: m.record_category,
    dataFields: calculateFieldCompleteness(m), // Percentage of key fields populated (0-100)
    industry: m.site?.industry?.replace(/_/g, ' '),
    state: m.site?.state || m.state,
    equipmentType: firstEquip.type || 'Unknown',
    equipmentAge: firstEquip.age_years,
    capacity: firstEquip.capacity_tons,
    refrigerant: firstEquip.refrigerant,
    efficiencyPct: m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct,
    kwPerTonBefore: m.measurements?.kw_per_ton?.before,
    kwPerTonAfter: m.measurements?.kw_per_ton?.after,
    cop: m.measurements?.cop,
    annualSavings: m.financial?.annual_savings,
    paybackMonths: m.financial?.payback_months,
    treatmentCost: m.financial?.treatment_cost,
    peName: m.certification?.pe_name || m.pe_name,
    methodology: m.certification?.methodology,
    testDate: m.test_date,
    raw: m // Keep raw data for detail view
  }
}

// Export data as CSV
export function exportToCsv(measurements, filename = 'corpus_data.csv') {
  const formatted = measurements.map(formatMeasurement)

  const headers = [
    'ID', 'Source', 'Quality', 'Category', 'Industry', 'State',
    'Equipment Type', 'Age (years)', 'Capacity (tons)', 'Refrigerant',
    'Efficiency %', 'kW/ton Before', 'kW/ton After', 'COP',
    'Annual Savings ($)', 'Payback (months)', 'PE Name', 'Methodology', 'Test Date'
  ]

  const rows = formatted.map(m => [
    m.id, m.source, m.quality, m.category, m.industry, m.state,
    m.equipmentType, m.equipmentAge, m.capacity, m.refrigerant,
    m.efficiencyPct?.toFixed(1), m.kwPerTonBefore?.toFixed(3), m.kwPerTonAfter?.toFixed(3), m.cop?.toFixed(2),
    m.annualSavings, m.paybackMonths, m.peName, m.methodology, m.testDate
  ])

  const csv = [headers, ...rows].map(row => row.join(',')).join('\n')

  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

// Export data as JSONL (for ML pipelines)
export function exportToJsonl(measurements, filename = 'corpus_data.jsonl') {
  const jsonl = measurements.map(m => JSON.stringify(m)).join('\n')

  const blob = new Blob([jsonl], { type: 'application/jsonl' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

// Transform corpus record to dashboard case study format (snake_case)
// This matches the format previously in src/data/caseStudies.json
export function transformToCaseStudy(record) {
  const eqArray = getEquipmentArray(record)
  const firstEquip = eqArray[0] || {}

  return {
    id: record.study_id,
    source: record.source_file,
    quality: record.extraction_quality,
    industry: record.site?.industry || null,
    state: record.site?.state || record.state || null,
    equipment_type: firstEquip.type || null,
    equipment_age: firstEquip.age_years || null,
    capacity_tons: firstEquip.capacity_tons || null,
    efficiency_pct: record.measurements?.efficiency_improvement_pct ||
                    record.measurements?.kw_per_ton?.improvement_pct || null,
    kw_per_ton_before: record.measurements?.kw_per_ton?.before || record.measurements?.kw_per_ton_before || null,
    kw_per_ton_after: record.measurements?.kw_per_ton?.after || record.measurements?.kw_per_ton_after || null,
    annual_savings: record.financial?.annual_savings || null,
    payback_months: record.financial?.payback_months || null,
    pe_name: record.certification?.pe_name || record.pe_name || null,
    test_date: record.test_date || null,
    category: record.record_category || null,
    refrigerant: firstEquip.refrigerant || null,
    manufacturer: firstEquip.manufacturer || null,
    certification_type: record.certification?.methodology || null
  }
}

// Load case studies in dashboard format (replaces static caseStudies.json import)
// This is the main entry point for components that need case study data
let caseStudiesCache = null
let caseStudiesPromise = null

export async function loadCaseStudies() {
  // Return cached data if available
  if (caseStudiesCache) {
    return caseStudiesCache
  }

  // Prevent multiple simultaneous fetches
  if (caseStudiesPromise) {
    return caseStudiesPromise
  }

  caseStudiesPromise = (async () => {
    try {
      const records = await loadMeasurements()
      caseStudiesCache = records.map(transformToCaseStudy)
      console.log(`Loaded and transformed ${caseStudiesCache.length} case studies`)
      return caseStudiesCache
    } catch (err) {
      console.error('Failed to load case studies:', err)
      caseStudiesPromise = null
      throw err
    }
  })()

  return caseStudiesPromise
}

// Load stats in dashboard format (replaces static stats.json import)
export async function loadDashboardStats() {
  const [manifest, caseStudies] = await Promise.all([
    loadManifest(),
    loadCaseStudies()
  ])

  // Calculate stats from loaded data
  const efficiencies = caseStudies.map(c => c.efficiency_pct).filter(Boolean)
  const savings = caseStudies.map(c => c.annual_savings).filter(Boolean)

  return {
    total_measurements: caseStudies.length,
    mean_efficiency: efficiencies.length > 0
      ? (efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length).toFixed(1)
      : 0,
    total_cost_savings: savings.reduce((a, b) => a + b, 0),
    us_states: new Set(caseStudies.map(c => c.state).filter(Boolean)).size,
    countries: manifest.geographic?.countries || 3,
    total_locations: manifest.geographic?.locations || caseStudies.length,
    co2_avoided: manifest.environmental?.co2_avoided_tonnes || 0
  }
}

// Get geographic data for maps
export async function loadGeographic() {
  const caseStudies = await loadCaseStudies()

  // Extract unique locations with coordinates
  const locations = []
  const stateCoords = {
    'AL': [32.3182, -86.9023], 'AK': [64.2008, -152.4937], 'AZ': [34.0489, -111.0937],
    'AR': [35.2010, -91.8318], 'CA': [36.7783, -119.4179], 'CO': [39.5501, -105.7821],
    'CT': [41.6032, -73.0877], 'DE': [38.9108, -75.5277], 'FL': [27.6648, -81.5158],
    'GA': [32.1656, -82.9001], 'HI': [19.8968, -155.5828], 'ID': [44.0682, -114.7420],
    'IL': [40.6331, -89.3985], 'IN': [40.2672, -86.1349], 'IA': [41.8780, -93.0977],
    'KS': [39.0119, -98.4842], 'KY': [37.8393, -84.2700], 'LA': [30.9843, -91.9623],
    'ME': [45.2538, -69.4455], 'MD': [39.0458, -76.6413], 'MA': [42.4072, -71.3824],
    'MI': [44.3148, -85.6024], 'MN': [46.7296, -94.6859], 'MS': [32.3547, -89.3985],
    'MO': [37.9643, -91.8318], 'MT': [46.8797, -110.3626], 'NE': [41.4925, -99.9018],
    'NV': [38.8026, -116.4194], 'NH': [43.1939, -71.5724], 'NJ': [40.0583, -74.4057],
    'NM': [34.5199, -105.8701], 'NY': [43.2994, -74.2179], 'NC': [35.7596, -79.0193],
    'ND': [47.5515, -101.0020], 'OH': [40.4173, -82.9071], 'OK': [35.4676, -97.5164],
    'OR': [43.8041, -120.5542], 'PA': [41.2033, -77.1945], 'RI': [41.5801, -71.4774],
    'SC': [33.8361, -81.1637], 'SD': [43.9695, -99.9018], 'TN': [35.5175, -86.5804],
    'TX': [31.9686, -99.9018], 'UT': [39.3210, -111.0937], 'VT': [44.5588, -72.5778],
    'VA': [37.4316, -78.6569], 'WA': [47.7511, -120.7401], 'WV': [38.5976, -80.4549],
    'WI': [43.7844, -88.7879], 'WY': [43.0760, -107.2903], 'DC': [38.9072, -77.0369]
  }

  // Group by state and count
  const stateCounts = {}
  caseStudies.forEach(cs => {
    if (cs.state && stateCoords[cs.state]) {
      stateCounts[cs.state] = (stateCounts[cs.state] || 0) + 1
    }
  })

  Object.entries(stateCounts).forEach(([state, count]) => {
    const coords = stateCoords[state]
    if (coords) {
      locations.push({
        state,
        count,
        lat: coords[0],
        lng: coords[1]
      })
    }
  })

  return { locations }
}
